import React, { useState } from 'react';
import { Checkbox, FormControlLabel, FormLabel, Grid, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { ADMIN_MODE_FEATURE_FLAG } from '@/hooks/useAdminModeAccess';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { AdminFeatureFlagPicker } from '@/admin/shared/pickers/AdminFeatureFlagPicker';
import { FeatureFlagChip, sortFeatureFlagChips } from '@/admin/shared/chips/FeatureFlagChip';
import { UndoFieldButton } from '@/admin/shared/UndoFieldButton';

// adminModeEnabled's broker groups can't be changed (see useFeatureFlagRules)
const isAdminModeFlag = (flag) => flag?.feature === ADMIN_MODE_FEATURE_FLAG;

/**
 * The edit drawer's Feature flags section: the flags that are on for this broker group as
 * chips, then a picker for the ones that target it directly. The form's `featureFlags`
 * (flag ids) is the draft; Save adds or removes the broker group on each flag that changed.
 *
 * - Chips that target the broker group (the draft) can be removed with their ×, except
 *   adminModeEnabled. Flags on through its saved ledger ("Include ledger") and for everyone
 *   ("Include global"), both off to start, show when ticked, in their own colours, and
 *   can't be removed here.
 * - In the picker, global flags are marked with an info icon ("Globally enabled"), and
 *   adminModeEnabled can't be added or removed.
 */
export const FeatureFlagFields = ({ ledger }) => {
	const { values, initialValues, setFieldValue } = useFormikContext();
	const { data: flags = [] } = useListAllFeatureFlagsQuery();
	const [includeLedger, setIncludeLedger] = useState(false);
	const [includeGlobal, setIncludeGlobal] = useState(false);

	const isSaved = (flag) => initialValues.featureFlags.includes(flag?.id);
	const getSource = (flag) =>
		(values.featureFlags.includes(flag.id) && 'direct') ||
		(includeLedger && ledger && (flag.ledgers ?? []).includes(ledger) && 'ledger') ||
		(includeGlobal && flag.active && 'global');

	const chips = sortFeatureFlagChips(
		flags.map((flag) => ({ flag, source: getSource(flag) })).filter(({ source }) => source),
	);

	const removeFlag = (flagId) =>
		setFieldValue(
			'featureFlags',
			values.featureFlags.filter((id) => id !== flagId),
		);

	return (
		<>
			<Grid size={12}>
				<Typography variant='form_label'>Feature flags</Typography>
			</Grid>
			<Grid size={12}>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeLedger}
							onChange={(event) => setIncludeLedger(event.target.checked)}
						/>
					}
					label='Include ledger'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeGlobal}
							onChange={(event) => setIncludeGlobal(event.target.checked)}
						/>
					}
					label='Include global'
				/>
				<Stack direction='row' flexWrap='wrap' gap={0.5} alignItems='center'>
					{chips.length ? (
						chips.map(({ flag, source }) => (
							<FeatureFlagChip
								key={flag.id}
								flag={flag}
								source={source}
								{...(source === 'direct' &&
									!isAdminModeFlag(flag) && {
										onDelete: () => removeFlag(flag.id),
									})}
							/>
						))
					) : (
						<Typography variant='body2' color='text.secondary'>
							No feature flags to show
						</Typography>
					)}
					{/* Puts back the saved flags; the picker can't clear them all at once */}
					<UndoFieldButton name='featureFlags' />
				</Stack>
			</Grid>
			<Grid size={12}>
				<AdminFeatureFlagPicker
					ledger={ledger}
					value={values.featureFlags}
					onChange={(ids) => setFieldValue('featureFlags', ids)}
					// Flags are removed one at a time; the heading's undo puts back the saved ones
					disableClearable
					showCheckbox
					isOptionDisabled={(option) => !isSaved(option.flag) && isAdminModeFlag(option.flag)}
					isOptionFixed={(option) => isSaved(option.flag) && isAdminModeFlag(option.flag)}
					// The chips above show the selection, so the input doesn't repeat them
					renderValue={() => null}
				/>
			</Grid>
		</>
	);
};
